import { useCallback } from "react";
import {
  findFutureSecurityBySymbol,
  searchFutureSecurities,
} from "@/store/futureSecuritiesStoreACTIONS";
import {
  FUTURE_TICKET_DIRECTIONS,
  returnContractOptionLabel,
  returnFutureContractChanges,
} from "../helper/futureTicketHelper";
import {
  ITicketFieldsProps,
  returnReferenceDataOptions,
} from "../helper/ticketSharedHelper";
import ContractCountInput from "../ticketFields/ContractCountInput";
import DirectionSelect from "../ticketFields/DirectionSelect";
import ReferenceDataSelect from "../ticketFields/ReferenceDataSelect";
import TicketDateInput from "../ticketFields/TicketDateInput";
import TicketRow from "../ticketFields/TicketRow";
import AllocationSplits from "../ticketFields/AllocationSplits";
import TicketExternalIdRow from "../ticketFields/TicketExternalIdRow";
import { useFutureTicket } from "./useFutureTicket";
import { returnSettlementHolidayCalendars } from "../helper/holidayCalendarsHelper";
import {
  NO_RISK_MESSAGE,
  returnDV01SizeChanges,
  returnTicketDV01,
  returnTicketRisk,
} from "../helper/dv01TicketHelper";
import DV01Input from "../ticketFields/DV01Input";
import { IFutureTicket, TicketInstrument } from "@/store/tradeFromScratchStore";

// The future fields a Trade Creation leg row shows inline. Assumes the futures reference
// data is loaded - the host gates on it.
const FutureCoreFields = ({ ticketId, highlightedKeys }: ITicketFieldsProps) => {
  const { ticket, updateFutureTicket, futureSecurities, contract, requiredKeys } =
    useFutureTicket(ticketId);

  const isMissing = (key: string) => !!highlightedKeys?.has(key);
  const isRequired = (key: string) => requiredKeys.has(key);

  // One row, not the bond ISIN / name pair: a futures SecurityName is the ticker.
  const searchContractOptions = useCallback(
    (term: string, limit: number) =>
      returnReferenceDataOptions(
        searchFutureSecurities(term, limit),
        (future) => ({
          label: returnContractOptionLabel(future),
          value: future.bloombergSymbol,
        }),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [futureSecurities],
  );

  return (
    <div
      data-testid="FutureCoreFieldsContainer"
      id="FutureCoreFieldsContainer"
      className="flex flex-col gap-2"
    >
      <TicketRow
        label="Contract"
        testId="FutureTicketContractRow"
        isHighlighted={isMissing("bloombergSymbol")}
        isRequired={isRequired("bloombergSymbol")}
      >
        <ReferenceDataSelect
          testId="FutureTicketContractInput"
          placeholder="Search contract ..."
          selected={
            ticket.bloombergSymbol
              ? {
                  label: contract
                    ? returnContractOptionLabel(contract)
                    : ticket.bloombergSymbol,
                  value: ticket.bloombergSymbol,
                }
              : null
          }
          searchOptions={searchContractOptions}
          onSelect={(option) =>
            updateFutureTicket(
              returnFutureContractChanges(
                ticket.tradeDate,
                option ? findFutureSecurityBySymbol(option.value) : undefined,
                option?.value ?? "",
              ),
            )
          }
        />
      </TicketRow>

      <TicketRow
        label="Contracts"
        testId="FutureTicketContractCountRow"
        isHighlighted={isMissing("contractCount")}
        isRequired={isRequired("contractCount")}
      >
        <ContractCountInput
          testId="FutureTicketContractCountInput"
          value={ticket.contractCount}
          onCommit={(contractCount) => updateFutureTicket({ contractCount })}
        />
      </TicketRow>

      <TicketRow label="Risk (DV01)" testId="FutureTicketDV01Row">
        <DV01Input
          testId="FutureTicketDV01Input"
          value={returnTicketDV01(TicketInstrument.Future, ticket)}
          onCommit={(dv01) =>
            updateFutureTicket(
              returnDV01SizeChanges(TicketInstrument.Future, ticket, dv01) as Partial<IFutureTicket>,
            )
          }
          disabledReason={
            returnTicketRisk(TicketInstrument.Future, ticket) === null ? NO_RISK_MESSAGE : undefined
          }
        />
      </TicketRow>

      <AllocationSplits
        testIdPrefix="FutureTicket"
        portfolioCode={ticket.portfolioCode}
        onPortfolioChange={(portfolioCode) => updateFutureTicket({ portfolioCode })}
        isPortfolioRequired={isRequired("portfolioCode")}
        total={ticket.contractCount}
        splits={ticket.additionalAllocations}
        sizeKind="contracts"
        highlightedKeys={highlightedKeys}
        onChange={(additionalAllocations) => updateFutureTicket({ additionalAllocations })}
      />

      <TicketRow
        label="Direction"
        testId="FutureTicketDirectionRow"
        isHighlighted={isMissing("direction")}
        isRequired={isRequired("direction")}
      >
        <DirectionSelect
          testId="FutureTicketDirectionInput"
          value={ticket.direction}
          directions={FUTURE_TICKET_DIRECTIONS}
          onChange={(direction) => updateFutureTicket({ direction })}
        />
      </TicketRow>

      <TicketRow
        label="Settlement Date"
        testId="FutureTicketSettlementDateRow"
        isHighlighted={isMissing("settlementDate")}
        isRequired={isRequired("settlementDate")}
      >
        <TicketDateInput
          testId="FutureTicketSettlementDateInput"
          holidayCalendars={returnSettlementHolidayCalendars(TicketInstrument.Future, ticket)}
          value={ticket.settlementDate}
          onChange={(settlementDate) => updateFutureTicket({ settlementDate })}
        />
      </TicketRow>

      <TicketExternalIdRow
        testIdPrefix="FutureTicket"
        value={ticket.transactionNumber}
        onChange={(transactionNumber) => updateFutureTicket({ transactionNumber })}
      />
    </div>
  );
};

export default FutureCoreFields;
